import { useState, useEffect, useCallback } from "react";
import { Navbar } from "../components/layout/Navbar";
import { SubHeader } from "../components/layout/SubHeader";
import { SingleConverter } from "../components/converter/SingleConverter";
import { BatchImporter } from "../components/converter/BatchImporter";
import { SettingsModal } from "../components/settings/SettingsModal";
import { SigmaReference } from "../components/ui/SigmaReference";
import { useTweaks } from "../hooks/useTweaks";
import { useConverter } from "../hooks/useConverter";
import type { ConversionConfig } from "../types/sigwaz";
import { DEFAULT_CONFIG } from "../types/sigwaz";

const SAMPLE_YAML = `title: Suspicious PowerShell Download Cradle
id: 3b6ab547-8ec3-4eca-9a61-2149571c7f1b
status: experimental
description: Detects PowerShell download cradle patterns used for malware delivery.
author: SigWaz
date: 2024/01/15
logsource:
    category: process_creation
    product: windows
detection:
    selection_img:
        Image|endswith:
            - '\\\\powershell.exe'
            - '\\\\pwsh.exe'
    selection_cli:
        CommandLine|contains:
            - 'Invoke-WebRequest'
            - 'IWR'
            - 'DownloadString'
            - 'wget'
    condition: all of selection_*
falsepositives:
    - Legitimate software updaters
level: high
tags:
    - attack.execution
    - attack.t1059.001
    - attack.t1105`;

type Mode = "single" | "batch";

export default function ConverterPage() {
  const { tweaks, setTweak } = useTweaks();
  const [mode, setMode]           = useState<Mode>("single");
  const [yaml, setYaml]           = useState(SAMPLE_YAML);
  const [config, setConfig]       = useState<ConversionConfig>(DEFAULT_CONFIG);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [refOpen, setRefOpen]     = useState(false);
  const [batchTrigger, setBatchTrigger] = useState(0);
  const [batchReady, setBatchReady]     = useState(false);

  const { status, singleResult, batchResult, runSingle, runBatch, reset } = useConverter(config);

  const handleModeChange = useCallback((newMode: Mode) => {
    setMode(newMode);
    reset();
    setBatchReady(false);
    setBatchTrigger(0);
  }, [reset]);

  const doConvert = useCallback(() => {
    if (mode === "batch") {
      setBatchTrigger(t => t + 1);
      return;
    }
    const hasDocSeparator = /^---[ \t]*$/m.test(yaml);
    const rootTitleCount = (yaml.match(/^title[ \t]*:/mg) || []).length;
    const isMultiDoc = hasDocSeparator || rootTitleCount > 1;
    if (isMultiDoc) runBatch(yaml);
    else runSingle(yaml);
  }, [mode, yaml, runSingle, runBatch]);

  useEffect(() => {
    document.documentElement.dataset.page = "converter";
    document.title = "SigWaz";
    const desc = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    if (desc) desc.content = "Paste or upload Sigma YAML rules and convert them to Wazuh XML in one click. Single rule, multi-document, and ZIP batch modes. MITRE ATT&CK, PCRE2, custom field mapping.";
    const canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (canonical) canonical.href = "https://sigwaz.com/converter";
    return () => { delete document.documentElement.dataset.page; };
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
        e.preventDefault();
        doConvert();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [doConvert]);

  const converting = status.kind === "converting";

  return (
    <>
      {tweaks.aurora  && <div className="bg-layer bg-aurora" />}
      {tweaks.dotGrid && <div className="bg-layer bg-dots" />}

      <div className="conv-app">
        <Navbar
          isDark={!tweaks.lightMode}
          onThemeToggle={() => setTweak("lightMode", !tweaks.lightMode)}
          onRefOpen={() => setRefOpen(true)}
        />

        <SubHeader
          mode={mode}
          onModeChange={handleModeChange}
          onSettingsOpen={() => setSettingsOpen(true)}
          onConvert={doConvert}
          converting={converting}
          canConvert={mode === "single" ? true : batchReady}
        />

        {mode === "single" ? (
          <SingleConverter
            yaml={yaml}
            setYaml={setYaml}
            result={singleResult}
            batchResult={batchResult}
            ruleIdStart={config.ruleIdStart}
          />
        ) : (
          <BatchImporter
            config={config}
            convertTrigger={batchTrigger}
            onReadyChange={setBatchReady}
          />
        )}
      </div>

      {settingsOpen && (
        <SettingsModal
          config={config}
          onChange={setConfig}
          onClose={() => setSettingsOpen(false)}
        />
      )}

      {refOpen && <SigmaReference onClose={() => setRefOpen(false)} />}
    </>
  );
}
